import React, { useState, useEffect, useRef } from 'react';
import { Plus, MagnifyingGlass, Pencil, Trash, X, Check, Package, Upload } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Pagination, { usePagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const initialFormData = { reference: '', name: '', category: '', subcategory: '', compatible_brand: '', compatible_model: '', compatible_year: '', side: '', description: '', price: '', stock: 0, active: true, image_url: '', keywords: [] };

const AdminParts = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [stockCount, setStockCount] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const { currentPage, setCurrentPage, paginated, totalPages, totalItems, rangeStart, rangeEnd } = usePagination(parts, 20);

  useEffect(() => { fetchParts(); fetchCategories(); fetchStockCount(); }, [search, categoryFilter]);

  const fetchStockCount = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/stock/count`);
      setStockCount(r.data.count);
    } catch (e) { setStockCount(null); }
  };

  // Poll a background import job until it finishes. Resolves with the job result.
  const pollImportJob = async (jobId, tid) => {
    const maxAttempts = 240; // 240 * 3s = 12 min max
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, 3000));
      let job;
      try {
        const resp = await axios.get(`${API_URL}/api/admin/stock/import-status/${jobId}`);
        job = resp.data;
      } catch (e) {
        continue; // transient network error, keep polling
      }
      if (job.status === 'completed') {
        return job;
      }
      if (job.status === 'error') {
        throw new Error(job.error || 'Import échoué');
      }
      toast.loading('Import en base en cours... (ne fermez pas cette page)', { id: tid });
    }
    throw new Error("L'import prend trop de temps. Vérifiez le nombre de pièces plus tard.");
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Veuillez sélectionner un fichier .csv');
      return;
    }
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    const confirmed = window.confirm(
      `Importer "${file.name}" (${sizeMB} MB) ?\n\nCela remplace TOUT le stock existant (${stockCount || 0} pièces actuellement).\n\nUpload chunké par tranches de 5 MB.`
    );
    if (!confirmed) {
      e.target.value = '';
      return;
    }
    setImporting(true);
    
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = (crypto.randomUUID ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2)));
    const tid = toast.loading(`Upload chunké : 0/${totalChunks} parts...`);
    
    // Helper: upload one chunk with retry (3 attempts)
    const uploadChunk = async (idx) => {
      const start = idx * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const fd = new FormData();
          fd.append('upload_id', uploadId);
          fd.append('chunk_index', String(idx));
          fd.append('total_chunks', String(totalChunks));
          fd.append('file', blob, `chunk_${idx}.part`);
          await axios.post(`${API_URL}/api/admin/stock/upload-chunk`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
          return true;
        } catch (e) {
          lastErr = e;
          if (attempt < 3) await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
      throw lastErr;
    };
    
    try {
      // Upload all chunks
      for (let i = 0; i < totalChunks; i++) {
        await uploadChunk(i);
        toast.loading(`Upload chunké : ${i + 1}/${totalChunks} parts...`, { id: tid });
      }
      
      // Verify all chunks are present in MongoDB before finalize
      toast.loading('Vérification...', { id: tid });
      const check = await axios.get(`${API_URL}/api/admin/stock/chunks/${uploadId}`);
      const received = new Set(check.data.received_indices || []);
      const missing = [];
      for (let i = 0; i < totalChunks; i++) {
        if (!received.has(i)) missing.push(i);
      }
      if (missing.length > 0) {
        toast.loading(`Renvoi de ${missing.length} chunks manquants...`, { id: tid });
        for (const idx of missing) await uploadChunk(idx);
      }
      
      // Finalize: assemble + import (background job, poll for completion)
      toast.loading('Assemblage et import en base...', { id: tid });
      const fin = await axios.post(`${API_URL}/api/admin/stock/finalize-upload`, {
        upload_id: uploadId,
        total_chunks: totalChunks,
      }, { timeout: 120000 });

      const job = await pollImportJob(fin.data.job_id, tid);
      toast.success(`Import réussi : ${job.imported || 0} pièces (${job.skipped || 0} ignorées)`, { id: tid, duration: 6000 });
      fetchStockCount();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erreur inconnue';
      toast.error('Erreur : ' + detail, { id: tid, duration: 10000 });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleImportFromStorage = async () => {
    const confirmed = window.confirm(
      `Importer le CSV par défaut depuis Object Storage ?\n\nCela remplace TOUT le stock existant (${stockCount || 0} pièces).\n\nLecture directe depuis le stockage partagé : ~5-30 secondes.`
    );
    if (!confirmed) return;
    setImporting(true);
    const tid = toast.loading('Import depuis Object Storage...');
    try {
      const start = await axios.post(`${API_URL}/api/admin/stock/import-from-storage`, {}, { timeout: 120000 });
      const job = await pollImportJob(start.data.job_id, tid);
      toast.success(`Import réussi : ${job.imported || 0} pièces (${job.skipped || 0} ignorées)`, { id: tid, duration: 6000 });
      fetchStockCount();
    } catch (err) {
      toast.error('Erreur : ' + (err.response?.data?.detail || err.message), { id: tid, duration: 8000 });
    } finally {
      setImporting(false);
    }
  };

  const handleImportFromUrl = async () => {
    // Default URL points to the current backend's /api/uploads/ endpoint,
    // which falls back to Emergent Object Storage. Works across preview + production
    // because the CSV is stored in shared Object Storage (not on per-pod disk).
    const defaultUrl = `${API_URL}/api/uploads/stock_ecocasse.csv`;
    const url = window.prompt(
      'URL du CSV à importer :\n\n(Le fichier par défaut est sauvegardé dans Emergent Object Storage et accessible depuis preview comme production)',
      defaultUrl
    );
    if (!url) return;
    if (!url.startsWith('http')) {
      toast.error('URL invalide');
      return;
    }
    const confirmed = window.confirm(
      `Télécharger et importer depuis :\n${url}\n\nCela remplace TOUT le stock existant (${stockCount || 0} pièces).\n\nL'opération peut prendre 1-3 minutes.`
    );
    if (!confirmed) return;
    setImporting(true);
    const tid = toast.loading('Téléchargement + import en cours...');
    try {
      const start = await axios.post(`${API_URL}/api/admin/stock/import-from-url`, { url }, {
        timeout: 120000,
      });
      const job = await pollImportJob(start.data.job_id, tid);
      toast.success(`Import réussi : ${job.imported || 0} pièces (${job.skipped || 0} ignorées)`, { id: tid, duration: 6000 });
      fetchStockCount();
    } catch (err) {
      toast.error('Erreur d\'import : ' + (err.response?.data?.detail || err.message), { id: tid, duration: 8000 });
    } finally {
      setImporting(false);
    }
  };

  const fetchParts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      const response = await axios.get(`${API_URL}/api/parts?${params}`);
      setParts(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try { const response = await axios.get(`${API_URL}/api/parts/categories`); setCategories(response.data); }
    catch (error) { console.error('Error:', error); }
  };

  const openCreateDialog = () => { setEditingPart(null); setFormData(initialFormData); setDialogOpen(true); };
  const openEditDialog = (part) => { setEditingPart(part); setFormData({ ...part, price: part.price || '', keywords: part.keywords || [] }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData, price: formData.price ? parseFloat(formData.price) : null, stock: parseInt(formData.stock) || 0 };
      if (editingPart) { await axios.put(`${API_URL}/api/parts/${editingPart.id}`, data); toast.success('Pièce mise à jour'); }
      else { await axios.post(`${API_URL}/api/parts`, data); toast.success('Pièce créée'); }
      setDialogOpen(false);
      fetchParts();
    } catch (error) { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Supprimer cette pièce ?')) return;
    try { await axios.delete(`${API_URL}/api/parts/${partId}`); toast.success('Pièce supprimée'); fetchParts(); }
    catch (error) { toast.error('Erreur'); }
  };

  return (
    <AdminLayout title="Catalogue de Pièces">
      {/* Stock Ecocasse banner */}
      <div className="bg-gradient-to-r from-[#E63946]/5 to-white border border-[#E63946]/30 p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="stock-banner">
        <div className="flex items-center gap-3">
          <Package size={28} className="text-[#E63946]" weight="fill" />
          <div>
            <p className="font-bold text-gray-900 text-sm">Stock Ecocasse (CSV Opisto)</p>
            <p className="text-gray-500 text-xs">
              {stockCount === null ? 'Chargement...' :
               stockCount === 0 ? <span className="text-[#E63946] font-semibold">⚠ Aucune pièce importée — importez votre CSV pour activer le matching automatique</span> :
               <><strong className="text-[#E63946]">{stockCount.toLocaleString('fr-FR')}</strong> pièces disponibles pour le matching avec les analyses</>}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportCSV}
            className="hidden"
            data-testid="csv-file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="bg-[#E63946] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center gap-2 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="import-csv-btn"
          >
            {importing ? (
              <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Import en cours...</>
            ) : (
              <><Upload size={16} weight="bold" />{stockCount === 0 ? 'Importer le CSV' : 'Réimporter le CSV'}</>
            )}
          </button>
          <button
            onClick={handleImportFromStorage}
            disabled={importing}
            className="bg-[#2A9D8F] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center gap-2 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="import-storage-btn"
            title="Import direct depuis Object Storage (rapide, recommandé)"
          >
            Object Storage
          </button>
          <button
            onClick={handleImportFromUrl}
            disabled={importing}
            className="bg-white border border-[#E63946] text-[#E63946] px-5 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center gap-2 hover:bg-[#E63946]/5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="import-url-btn"
            title="Pour les gros CSV > 30 MB qui ne passent pas par le navigateur"
          >
            Depuis URL
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative"><MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" /><input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="w-full bg-white border border-gray-200 text-gray-900 pl-12 pr-4 py-3" data-testid="search-parts" /></div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="bg-white border border-gray-200 text-gray-900 px-4 py-3 min-w-[200px]" data-testid="filter-category"><option value="">Toutes les catégories</option>{categories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}</select>
        <button onClick={openCreateDialog} className="bg-[#E63946] text-white px-6 py-3 font-bold text-sm uppercase flex items-center gap-2 hover:bg-[#D90429]" data-testid="add-part-btn"><Plus size={20} />Ajouter</button>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-200 bg-gray-50"><th className="text-left p-4 text-gray-500 text-xs uppercase">Référence</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Nom</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Catégorie</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Côté</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Prix</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Stock</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Statut</th><th className="text-right p-4 text-gray-500 text-xs uppercase">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={8} className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              : paginated.length > 0 ? paginated.map((part, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-4 text-gray-500 text-sm font-mono">{part.reference}</td>
                  <td className="p-4 text-gray-900 font-medium">{part.name}</td>
                  <td className="p-4 text-gray-500 text-sm">{part.category}</td>
                  <td className="p-4 text-gray-500 text-sm capitalize">{part.side || '-'}</td>
                  <td className="p-4 text-[#2A9D8F] font-medium">{part.price ? `${part.price.toFixed(2)} €` : '-'}</td>
                  <td className="p-4 text-gray-600">{part.stock}</td>
                  <td className="p-4">{part.active ? <span className="px-2 py-1 text-xs font-bold uppercase bg-[#2A9D8F]/10 text-[#2A9D8F]">Actif</span> : <span className="px-2 py-1 text-xs font-bold uppercase bg-gray-100 text-gray-400">Inactif</span>}</td>
                  <td className="p-4"><div className="flex items-center justify-end gap-2"><button onClick={() => openEditDialog(part)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100" data-testid={`edit-part-${i}`}><Pencil size={16} /></button><button onClick={() => handleDelete(part.id)} className="p-2 text-gray-400 hover:text-[#E63946] hover:bg-[#E63946]/10" data-testid={`delete-part-${i}`}><Trash size={16} /></button></div></td>
                </tr>
              )) : <tr><td colSpan={8} className="p-8 text-center"><Package size={48} className="text-gray-300 mx-auto mb-4" /><p className="text-gray-400">Aucune pièce trouvée</p></td></tr>}
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="px-4 pb-2">
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} rangeStart={rangeStart} rangeEnd={rangeEnd} totalItems={totalItems} />
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl">{editingPart ? 'Modifier la pièce' : 'Nouvelle pièce'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-600 text-sm mb-2">Référence *</label><input type="text" value={formData.reference} onChange={(e) => setFormData(p => ({ ...p, reference: e.target.value }))} required className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
              <div><label className="block text-gray-600 text-sm mb-2">Nom *</label><input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-600 text-sm mb-2">Catégorie *</label><input type="text" value={formData.category} onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} required className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
              <div><label className="block text-gray-600 text-sm mb-2">Sous-catégorie</label><input type="text" value={formData.subcategory} onChange={(e) => setFormData(p => ({ ...p, subcategory: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="block text-gray-600 text-sm mb-2">Prix (€)</label><input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
              <div><label className="block text-gray-600 text-sm mb-2">Stock</label><input type="number" value={formData.stock} onChange={(e) => setFormData(p => ({ ...p, stock: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3" /></div>
              <div><label className="block text-gray-600 text-sm mb-2">Côté</label><select value={formData.side} onChange={(e) => setFormData(p => ({ ...p, side: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3"><option value="">N/A</option><option value="avant">Avant</option><option value="arriere">Arrière</option><option value="gauche">Gauche</option><option value="droite">Droite</option></select></div>
            </div>
            <div><label className="block text-gray-600 text-sm mb-2">Description</label><textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} className="w-full bg-gray-50 border border-gray-300 text-gray-900 p-3 resize-none" /></div>
            <div className="flex items-center gap-3"><input type="checkbox" checked={formData.active} onChange={(e) => setFormData(p => ({ ...p, active: e.target.checked }))} className="w-5 h-5" /><span className="text-gray-900">Actif</span></div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={() => setDialogOpen(false)} className="px-6 py-3 border border-gray-300 text-gray-700 text-sm">Annuler</button>
              <button type="submit" disabled={saving} className="px-6 py-3 bg-[#E63946] text-white text-sm font-bold uppercase hover:bg-[#D90429] disabled:opacity-50 flex items-center gap-2">{saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Enregistrement...</> : <><Check size={18} />Enregistrer</>}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminParts;
